/** @format */
import { TFunction } from 'i18next';

import { hasValue } from '@toucan/client/global/application';

import { removeMarkdownCharacters } from '@toucan/client/shared/helpers/markdown';

export enum MetaFactoryType {
    HOMEPAGE,
    SELL_FORM,
    CATEGORY,
    BROWSE_BRANDS,
    BRAND,
    MODEL,
    ORIGINAL_CONTENT,
    PRODUCT,
    DEFAULT,
}

type MetaProvider = () => MetaMapping;

export type MetaMapping = {
    title: string;
    description: string;
};

export class MetaFactory {
    private readonly metaProvider: MetaProvider;

    public constructor(metaProvider: MetaProvider) {
        this.metaProvider = metaProvider;
    }

    public getMeta(): MetaMapping {
        return this.metaProvider();
    }
}

export const metaFactories = {
    [MetaFactoryType.HOMEPAGE]: (t: TFunction): MetaFactory =>
        new MetaFactory(() => {
            return {
                title: t('MPB | Buy or Sell Used Cameras & Lenses'),
                description: t(
                    'MPB is the largest global platform to buy, sell and trade used photo and video kit. The simple, safe and circular way to trade, upgrade and get paid.'
                ),
            };
        }),
    [MetaFactoryType.SELL_FORM]: (
        t: TFunction,
        metaTitle: string = '',
        metaDescription: string = ''
    ): MetaFactory =>
        new MetaFactory(() => {
            const title = hasValue(metaTitle)
                ? `${metaTitle} | MPB`
                : t('Sell or Trade Used Cameras, Lenses & Accessories | MPB');

            const description = hasValue(metaDescription)
                ? metaDescription
                : t(
                      'Trade in photo & video equipment hassle-free with our five star rated service! Get a commitment-free quote online today.'
                  );

            return {
                title,
                description,
            };
        }),
    [MetaFactoryType.CATEGORY]: (
        t: TFunction,
        {
            metaTitle = '',
            metaDescription = '',
            categoryName = '',
            categoryDescription = '',
        }
    ): MetaFactory =>
        new MetaFactory(() => {
            // category has 2 fallbacks, first is the taxonomy driven categoryDescription
            // if that is undefined we have the generic copy
            const fallbackDescription = categoryDescription
                ? categoryDescription
                : t(
                      "Buy, sell and trade in used {{categoryName}}. All with a 12 month warranty. Great photo and video kit that doesn't cost the earth.",
                      {
                          categoryName,
                      }
                  );

            // first try and use the metaDescription, otherwise fallback
            const description = hasValue(metaDescription)
                ? metaDescription
                : fallbackDescription;

            return {
                title: `${
                    hasValue(metaTitle) ? metaTitle : categoryName
                } | MPB`,
                description,
            };
        }),
    [MetaFactoryType.BRAND]: (
        t: TFunction,
        {
            metaTitle = '',
            metaDescription = '',
            brandName = '',
            brandDescription = '',
        }
    ): MetaFactory =>
        new MetaFactory(() => {
            // brand has 2 fallbacks, first is the taxonomy driven brandDescription
            // if that is undefined we have the generic copy
            const fallbackDescription = brandDescription
                ? brandDescription
                : t(
                      "Buy, sell and trade in used {{brandName}} equipment. All with a 12 month warranty. Great photo and video kit that doesn't cost the earth.",
                      {
                          brandName,
                      }
                  );

            // first try and use the metaDescription, otherwise fallback
            const description = hasValue(metaDescription)
                ? metaDescription
                : fallbackDescription;

            return {
                title: `${hasValue(metaTitle) ? metaTitle : brandName} | MPB`,
                description,
            };
        }),
    [MetaFactoryType.MODEL]: (
        t: TFunction,
        modelName: string = '',
        isNew: boolean
    ): MetaFactory =>
        new MetaFactory(() => {
            if (isNew) {
                return {
                    title: t('{{modelName}} | MPB', {
                        modelName,
                    }),
                    description: t(
                        'Buy a {{modelName}}, trade-in your own, or sell for cash, with MPB.',
                        { modelName }
                    ),
                };
            }

            return {
                title: t('Used {{modelName}} | MPB', {
                    modelName,
                }),
                description: t(
                    'Buy a used {{modelName}}, trade-in your own, or sell for cash, with MPB.',
                    {
                        modelName,
                    }
                ),
            };
        }),
    [MetaFactoryType.ORIGINAL_CONTENT]: (t: TFunction): MetaFactory =>
        new MetaFactory(() => {
            return {
                title: t(
                    'MPB Original Content Hub for Blog Article Pages | MPB'
                ),
                description: t(
                    'Discover our latest photography & video articles via the MPB content hub: Competitions, Interviews, EDU, Kit Guides, Sustainability, Tips, Videos & Podcasts'
                ),
            };
        }),
    [MetaFactoryType.BROWSE_BRANDS]: (t: TFunction): MetaFactory =>
        new MetaFactory(() => {
            return {
                title: t('Shop by brand | MPB'),
                description: t(
                    'View our most popular brands or browse our stocked brands alphabetically'
                ),
            };
        }),
    [MetaFactoryType.PRODUCT]: (
        t: TFunction,
        productName: string = '',
        isNew: boolean
    ): MetaFactory =>
        new MetaFactory(() => {
            if (isNew) {
                return {
                    title: t('{{productName}} | MPB', {
                        productName,
                    }),
                    description: t(
                        'Buy a {{productName}}, trade-in your own, or sell for cash, with MPB.',
                        { productName }
                    ),
                };
            }

            return {
                title: t('Used {{productName}} | MPB', {
                    productName,
                }),
                description: t(
                    'Buy a used {{productName}}, trade-in your own, or sell for cash, with MPB.',
                    {
                        productName,
                    }
                ),
            };
        }),
    [MetaFactoryType.DEFAULT]: (
        t: TFunction,
        pageName?: string,
        title?: string | null,
        description?: string | null
    ): MetaFactory =>
        new MetaFactory(() => {
            // the pageName can sometimes contain markdown, we don't want to render these in
            // the page title, so they should be removed
            const selectedTitle = title ?? removeMarkdownCharacters(pageName);
            return {
                title: selectedTitle
                    ? t('{{title}} | MPB', {
                          title: selectedTitle,
                          interpolation: { escapeValue: false },
                      })
                    : t('MPB'),
                description:
                    description ??
                    t(
                        'MPB is the largest global platform to buy, sell and trade used photo and video kit. The simple, safe and circular way to trade, upgrade and get paid.'
                    ),
            };
        }),
};
